<template>
  <NamiModal v-model="isModalOpen" large darker>
    <NamiModalTitle @close="isModalOpen = false">
      {{ t(key(`title.${mode}`), bookmarksToEdit.length) }}
    </NamiModalTitle>

    <div class="space-y-4">
      <div class="space-y-2">
        <div
          v-for="(bookmark, index) in bookmarksToEdit"
          :key="bookmark.id"
          class="p-4 rounded-md border border-neutral-200 dark:border-neutral-800 space-y-2"
        >
          <NovelBookmarkCard
            :bookmark
            no-options
            :no-name="!isBookmark(bookmark)"
          />
          <NamiTextArea
            v-model="commentsPerBookmark[index]"
            :placeholder="t(key('note_placeholder'))"
          />
        </div>
      </div>

      <div class="flex justify-between">
        <NamiButton button-type="primary" @button-click="finalize">
          {{ t(key("buttons.update")) }}
        </NamiButton>

        <div class="flex gap-2">
          <NamiButton button-type="danger" @button-click="doDelete">
            {{ t(key("buttons.delete")) }}
          </NamiButton>
          <NamiButton button-type="secondary" @button-click="abandon">
            {{ t(key("buttons.cancel")) }}
          </NamiButton>
        </div>
      </div>
    </div>
  </NamiModal>
</template>

<script setup lang="ts">
import { BOOKMARK_POSITION, type NovelBookmarkEntity } from "~/src/api";

type EditMode = "highlight" | "bookmark";

const emit = defineEmits<{
  (
    e: "updateBookmarks",
    payload: { bookmarkId: string; comment?: string }[],
  ): void;
  (
    e: "updateHighlights",
    payload: { highlightId: string; comment?: string }[],
  ): void;
  (e: "deleteBookmarks", payload: NovelBookmarkEntity[]): void;
  (e: "deleteHighlights", payload: NovelBookmarkEntity[]): void;
}>();

const { t } = useI18n();
const key = useI18nKey("components.novel.bookmark.novelBookmarkEditModal");

const isModalOpen = ref(false);
const bookmarksToEdit = shallowRef<NovelBookmarkEntity[]>([]);
const commentsPerBookmark = ref<string[]>([]);
const mode = ref<EditMode>("bookmark");

watch(
  bookmarksToEdit,
  (newBookmarks) => {
    commentsPerBookmark.value = newBookmarks.map(
      (b) => b.attributes.comment ?? "",
    );
  },
  { immediate: true },
);

function finalize() {
  if (mode.value === "bookmark") {
    emit(
      "updateBookmarks",
      bookmarksToEdit.value.map((b, i) => ({
        bookmarkId: b.id,
        comment: commentsPerBookmark.value[i],
      })),
    );
  } else {
    emit(
      "updateHighlights",
      bookmarksToEdit.value.map((b, i) => ({
        highlightId: b.id,
        comment: commentsPerBookmark.value[i],
      })),
    );
  }

  isModalOpen.value = false;
  reset();
}

function abandon() {
  isModalOpen.value = false;
  reset();
}

function doDelete() {
  if (mode.value === "bookmark") {
    emit("deleteBookmarks", bookmarksToEdit.value);
  } else {
    emit("deleteHighlights", bookmarksToEdit.value);
  }

  isModalOpen.value = false;
  reset();
}

function reset() {
  setTimeout(() => {
    bookmarksToEdit.value = [];
    commentsPerBookmark.value = [];
  }, 300);
}

function isBookmark(bookmark: NovelBookmarkEntity) {
  return bookmark.attributes.position === BOOKMARK_POSITION;
}

defineExpose({
  open: (editMode: EditMode, bookmarks: NovelBookmarkEntity[]) => {
    bookmarksToEdit.value = bookmarks;
    isModalOpen.value = true;
    mode.value = editMode;
  },
});
</script>
